import { useEffect } from "react";
import { AppModal } from "../../../../../_CORE/APP/Templates/_UI/Modal/_index";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import { FormSection, FieldInput, FieldTextarea, FieldSelect, FieldSwitch, useForm } from "../../../../../_CORE/APP/Templates/_UI/Form/_index";
import { useApiActionMutation } from "../../../../../_CORE/APP/Hooks/Hooks/_Actions/_useApiActionMutation";
import { CONTACT_GROUP_CONSTANTS } from "../_MODULES/_API";
import { isDuplicateRecord } from "../../../../_UI/recordUniqueness";

export const CONTACT_GROUP_COLORS = [
    { value: "GRAPHITE", label: "Grafito" },
    { value: "SLATE", label: "Pizarra" },
    { value: "BLUE", label: "Azul" },
    { value: "GREEN", label: "Verde" },
    { value: "AMBER", label: "Ámbar" },
    { value: "RED", label: "Rojo" },
    { value: "VIOLET", label: "Violeta" },
    { value: "ROSE", label: "Rosa" },
    { value: "OTHER", label: "Otro / personalizado" },
];

export const CONTACT_GROUP_ICONS = [
    { value: "USERS", label: "Personas / general" },
    { value: "WORK", label: "Trabajo" },
    { value: "FAMILY", label: "Familia" },
    { value: "CLIENTS", label: "Clientes" },
    { value: "PROVIDERS", label: "Proveedores" },
    { value: "FRIENDS", label: "Amigos / conocidos" },
    { value: "HEALTH", label: "Salud / profesionales" },
    { value: "SERVICES", label: "Servicios / comercios" },
    { value: "EDUCATION", label: "Educación / docentes" },
    { value: "LEGAL", label: "Legal / trámites" },
    { value: "FINANCE", label: "Finanzas / bancos" },
    { value: "SPORT", label: "Deporte / gimnasio" },
    { value: "MUSIC", label: "Música / artistas" },
    { value: "TECH", label: "Tecnología / IT" },
    { value: "EMERGENCY", label: "Emergencias" },
    { value: "TRAVEL", label: "Viajes" },
    { value: "BUSINESS", label: "Negocios / empresas" },
    { value: "OTHER", label: "Otro / personalizado" },
];

const EMPTY = { name: "", description: "", color: "GRAPHITE", icon: "USERS", isFavorite: false };

export const ContactGroupModal = ({ isOpen, group = null, groups = [], onClose, onSaved }) => {
    const editing = !!group?.id;
    const { actionMutate, useActionApiIsLoading } = useApiActionMutation({ isToastError: true, isToastSuccess: true, minTimeMutate: 180 });
    const FORM = useForm({
        initialValues: EMPTY,
        schema: {},
        onSubmit: async (values) => {
            if (!String(values.name || "").trim()) {
                FORM.setError("name", "Escribí un nombre para el grupo", { focus: true });
                return;
            }
            if (isDuplicateRecord({ rows: groups, value: values.name, currentId: group?.id })) {
                FORM.setError("name", "Ya existe un grupo con ese nombre", { focus: true });
                return;
            }
            const data = {
                group: { name: values.name, description: values.description, color: values.color, icon: values.icon },
                isFavorite: !!values.isFavorite,
            };
            const response = editing
                ? await actionMutate({ method: "PATCH", endpoint: CONTACT_GROUP_CONSTANTS.UPDATE, id: group.id, data, successMessage: "Grupo actualizado" })
                : await actionMutate({ method: "POST", endpoint: CONTACT_GROUP_CONSTANTS.CREATE, data, successMessage: "Grupo creado" });
            if (!response.success) { FORM.applyApiError(response); return; }
            onSaved?.(response.data, editing);
            onClose?.();
        },
    });

    useEffect(() => {
        if (!isOpen) return;
        FORM.reset(editing ? { ...EMPTY, ...group, isFavorite: !!group.isFavorite } : EMPTY);
    }, [isOpen, group]); // eslint-disable-line react-hooks/exhaustive-deps

    return <AppModal isOpen={isOpen} onClose={onClose} isDirty={FORM.isDirty} size="md"
        title={editing ? "Editar grupo de contactos" : "Nuevo grupo de contactos"}
        description="Agrupá la agenda para encontrar personas sin tener todos los contactos mezclados."
        footer={<>
            <AppButton variant="ghost" tone="neutral" onClick={onClose}>Cancelar</AppButton>
            <AppButton onClick={FORM.onSubmit} isLoading={useActionApiIsLoading}>{editing ? "Guardar cambios" : "Crear grupo"}</AppButton>
        </>}>
        <FieldSwitch id="contact_group_favorite" label="Destacado" text="Mostrar este grupo primero"
            isChecked={!!FORM.values.isFavorite} onChange={(event) => FORM.setValue("isFavorite", event.target.checked)} />
        <FormSection title="Grupo" columns={2}>
            <FieldInput {...FORM.field("name")} label="Nombre" placeholder="Ej: Trabajo, Familia, Médicos..." span={2} />
            <FieldSelect {...FORM.field("icon")} label="Tipo visual" options={CONTACT_GROUP_ICONS} />
            <FieldSelect {...FORM.field("color")} label="Identificador" options={CONTACT_GROUP_COLORS} />
            <FieldTextarea {...FORM.field("description")} label="Descripción" rows={3} span={2} />
        </FormSection>
    </AppModal>;
};
